// src/pages/Dashboard.js
import React, { useMemo, useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Area,
  AreaChart,
} from "recharts";
import { FaSearch, FaSync } from "react-icons/fa";
import "./Dashboard.css";
import DashboardLayout from "../layouts/DashboardLayout";
import getApiUrl from "../utils/api";

const mainChartMargin = { top: 20, right: 30, left: 0, bottom: 0 };

const MainTaskStatusChart = React.memo(({ data }) => (
  <ResponsiveContainer width="100%" height={320}>
    <AreaChart data={data} margin={mainChartMargin}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="month" />
      <YAxis />
      <Tooltip />
      <Legend iconType="circle" />
      <Area
        type="monotone"
        dataKey="Completed"
        stroke="#7be7a7"
        fill="#d6fbe7"
        fillOpacity={0.5}
        dot={{ r: 6, fill: "#7be7a7", stroke: "#fff", strokeWidth: 2 }}
        activeDot={{ r: 8 }}
      />
      <Area
        type="monotone"
        dataKey="Delegated"
        stroke="#ffe29e"
        fill="#fff7d6"
        fillOpacity={0.5}
        dot={{ r: 6, fill: "#ffe29e", stroke: "#fff", strokeWidth: 2 }}
        activeDot={{ r: 8 }}
      />
      <Area
        type="monotone"
        dataKey="Revalidate"
        stroke="#f7b6d2"
        fill="#fde6ef"
        fillOpacity={0.5}
        dot={{ r: 6, fill: "#f7b6d2", stroke: "#fff", strokeWidth: 2 }}
        activeDot={{ r: 8 }}
      />
      <Area
        type="monotone"
        dataKey="Pending"
        stroke="#b3b7f7"
        fill="#e6e8fd"
        fillOpacity={0.5}
        dot={{ r: 6, fill: "#b3b7f7", stroke: "#fff", strokeWidth: 2 }}
        activeDot={{ r: 8 }}
      />
      <Area
        type="monotone"
        dataKey="Planned"
        stroke="#b3f7f7"
        fill="#e6fafd"
        fillOpacity={0.5}
        dot={{ r: 6, fill: "#b3f7f7", stroke: "#fff", strokeWidth: 2 }}
        activeDot={{ r: 8 }}
      />
      <Area
        type="monotone"
        dataKey="WIP"
        stroke="#ffe29e"
        fill="#fff7d6"
        fillOpacity={0.5}
        dot={{ r: 6, fill: "#ffe29e", stroke: "#fff", strokeWidth: 2 }}
        activeDot={{ r: 8 }}
      />
    </AreaChart>
  </ResponsiveContainer>
));

const DeptBarChart = React.memo(({ data, color, label }) => (
  <ResponsiveContainer width="100%" height={120}>
    <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
      <XAxis type="number" hide />
      <YAxis
        dataKey="dept"
        type="category"
        width={120}
        tick={{ fontWeight: 600, fontSize: 16 }}
      />
      <Tooltip />
      <Bar
        dataKey="value"
        fill={color}
        radius={[8, 8, 8, 8]}
        isAnimationActive={true}
        animationDuration={900}
      >
        <LabelList
          dataKey="value"
          position="right"
          style={{ fontWeight: 700, fontSize: 18, fill: "#222" }}
        />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
));

const FilterBar = React.memo(
  ({ filters, departments, employees, onFilterChange, onSearch, onReset }) => (
    <div className="dashboard-filter-bar">
      <input
        type="date"
        className="dashboard-date-input"
        placeholder="From Date"
        value={filters.fromDate}
        onChange={(e) => onFilterChange("fromDate", e.target.value)}
      />
      <input
        type="date"
        className="dashboard-date-input"
        placeholder="To Date"
        value={filters.toDate}
        onChange={(e) => onFilterChange("toDate", e.target.value)}
      />
      <select
        className="dashboard-select"
        value={filters.department}
        onChange={(e) => onFilterChange("department", e.target.value)}
      >
        <option value="">Department: All</option>
        {departments.map((dept) => (
          <option key={dept.id} value={String(dept.id)}>
            {dept.name}
          </option>
        ))}
      </select>
      <select
        className="dashboard-select"
        value={filters.employee}
        onChange={(e) => onFilterChange("employee", e.target.value)}
      >
        <option value="">Employee: All</option>
        {employees.map((emp) => {
          const first = emp.FirstName ?? emp.first_name ?? "";
          const last = emp.LastName ?? emp.last_name ?? "";
          const baseLabel = `${first} ${last}`.trim() || emp.email || "Unnamed";
          const isSelf = Boolean(emp.isSelf);
          const displayLabel = isSelf
            ? `Me${baseLabel ? ` (${baseLabel})` : ""}`
            : baseLabel;
          const level =
            !isSelf && emp.hierarchyLevel === 1
              ? " (L1 report)"
              : !isSelf && emp.hierarchyLevel === 2
              ? " (L2 report)"
              : "";
          return (
            <option key={emp.id} value={String(emp.id)}>
              {displayLabel}
              {level}
            </option>
          );
        })}
      </select>
      <button className="dashboard-filter-btn" onClick={onSearch}>
        <FaSearch />
      </button>
      <button className="dashboard-reset-btn" onClick={onReset}>
        <FaSync />
      </button>
    </div>
  )
);

const StatusSummaryCards = React.memo(({ summary }) => (
  <div className="dashboard-status-cards">
    {summary.map((s) => (
      <div
        key={s.label}
        className="dashboard-status-card"
        style={{ background: s.color }}
      >
        <span className="dashboard-status-label">{s.label}</span>
        <span className="dashboard-status-value">{s.value}</span>
      </div>
    ))}
  </div>
));

const DeptChartContainer = React.memo(({ title, children }) => (
  <div className="dashboard-dept-chart">
    <h4>{title}</h4>
    {children}
  </div>
));

const normalizeTaskRecord = (task, { isSelfTask = false } = {}) => {
  if (!task) return null;

  const departmentId =
    task.department_id ||
    task.departmentId ||
    task.project?.department_id ||
    task.assignee?.department_id ||
    null;

  const normalizedDepartmentId = departmentId ? String(departmentId) : null;
  const normalizedAssigneeId = task.assigned_to
    ? String(task.assigned_to)
    : task.assignee_id
    ? String(task.assignee_id)
    : null;

  return {
    ...task,
    normalizedDepartmentId,
    normalizedAssigneeId,
    isSelfTask,
  };
};

const Dashboard = () => {
  // State for data and filters
  const [tasks, setTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [managedEmployees, setManagedEmployees] = useState([]);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [showLoader, setShowLoader] = useState(true);
  const [showCharts, setShowCharts] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    department: "",
    employee: "",
  });

  const currentUserId = currentUserInfo?.id || null;

  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (!stored) return;
      const parsed = JSON.parse(stored);
      const id =
        parsed?.id ||
        parsed?.userId ||
        parsed?.user_id ||
        parsed?.user?.id ||
        null;
      if (!id) return;

      const firstName =
        parsed?.FirstName ??
        parsed?.first_name ??
        parsed?.user?.FirstName ??
        "";
      const lastName =
        parsed?.LastName ??
        parsed?.last_name ??
        parsed?.user?.LastName ??
        "";
      const email = parsed?.email ?? parsed?.user?.email ?? "";
      const departmentId =
        parsed?.department_id ??
        parsed?.departmentId ??
        parsed?.department?.id ??
        null;

      setCurrentUserInfo({
        id: String(id),
        FirstName: firstName,
        LastName: lastName,
        email,
        department_id: departmentId ? String(departmentId) : null,
        hierarchyLevel: 0,
        isSelf: true,
      });
    } catch (err) {
      console.error("Error reading current user info:", err);
    }
  }, []);

  // Fetch direct and indirect reports for dropdown filters
  useEffect(() => {
    let cancelled = false;

    const loadManagedUsers = async () => {
      try {
        const res = await fetch(getApiUrl("api/users"), {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to load managed users (${res.status})`);
        }
        const payload = await res.json();
        if (cancelled) return;

        const normalized = (Array.isArray(payload) ? payload : []).map(
          (user) => {
            const first = user.FirstName ?? user.first_name ?? "";
            const last = user.LastName ?? user.last_name ?? "";
            const departmentId =
              user.department_id ??
              user.departmentId ??
              user.department?.id ??
              null;

            return {
              id: user.id,
              FirstName: first,
              LastName: last,
              email: user.email,
              department_id: departmentId ? String(departmentId) : null,
              hierarchyLevel: user.hierarchyLevel ?? null,
            };
          }
        );

        normalized.sort((a, b) => {
          const left = `${a.FirstName || ""} ${a.LastName || ""}`.trim();
          const right = `${b.FirstName || ""} ${b.LastName || ""}`.trim();
          return left.localeCompare(right);
        });

        let withSelf = normalized;
        if (currentUserInfo) {
          const exists = normalized.some(
            (emp) => String(emp.id) === currentUserInfo.id
          );

          if (exists) {
            withSelf = normalized.map((emp) =>
              String(emp.id) === currentUserInfo.id
                ? { ...emp, isSelf: true, hierarchyLevel: emp.hierarchyLevel ?? 0 }
                : emp
            );
          } else {
            withSelf = [
              {
                id: currentUserInfo.id,
                FirstName: currentUserInfo.FirstName,
                LastName: currentUserInfo.LastName,
                email: currentUserInfo.email,
                department_id: currentUserInfo.department_id,
                hierarchyLevel: 0,
                isSelf: true,
              },
              ...normalized,
            ];
          }
        }

        setManagedEmployees(withSelf);
      } catch (err) {
        console.error("Error fetching managed users:", err);
        if (!cancelled) {
          if (currentUserInfo) {
            setManagedEmployees([currentUserInfo]);
          } else {
            setManagedEmployees([]);
          }
        }
      }
    };

    loadManagedUsers();

    return () => {
      cancelled = true;
    };
  }, [currentUserInfo]);

  // Fetch all departments once
  useEffect(() => {
    let cancelled = false;

    const loadDepartments = async () => {
      try {
        const res = await fetch(getApiUrl("api/auth/departments"));
        if (!res.ok) {
          throw new Error(`Failed to load departments (${res.status})`);
        }
        const payload = await res.json();
        if (!cancelled) {
          setAllDepartments(Array.isArray(payload) ? payload : []);
        }
      } catch (err) {
        console.error("Error fetching departments:", err);
        if (!cancelled) {
          setAllDepartments([]);
        }
      }
    };

    loadDepartments();
    return () => {
      cancelled = true;
    };
  }, []);

  // Derive department options based on managed employees
  useEffect(() => {
    const managedDeptIds = new Set(
      managedEmployees
        .map((emp) => emp.department_id)
        .filter(Boolean)
        .map((id) => String(id))
    );

    tasks
      .map((task) => task.normalizedDepartmentId)
      .filter(Boolean)
      .forEach((id) => managedDeptIds.add(String(id)));

    const nextDepartments = allDepartments.filter((dept) =>
      managedDeptIds.has(String(dept.id))
    );

    setDepartments(nextDepartments);

    setFilters((prev) => {
      if (prev.department && !managedDeptIds.has(prev.department)) {
        return { ...prev, department: "" };
      }
      return prev;
    });
  }, [managedEmployees, allDepartments, tasks]);

  // Ensure selected employee remains within managed scope
  useEffect(() => {
    const managedIds = new Set(
      managedEmployees.map((emp) => String(emp.id))
    );

    setFilters((prev) => {
      if (prev.employee && !managedIds.has(prev.employee)) {
        return { ...prev, employee: "" };
      }
      return prev;
    });
  }, [managedEmployees]);

  // Fetch subordinate tasks and merge with current user's own tasks
  useEffect(() => {
    let cancelled = false;

    const fetchOwnTasks = async () => {
      if (!currentUserId) return [];
      try {
        const myRes = await fetch(
          getApiUrl(`api/task/myTask/${currentUserId}`),
          { credentials: "include" }
        );
        if (!myRes.ok) {
          return [];
        }
        const myPayload = await myRes.json();
        const myList = Array.isArray(myPayload) ? myPayload : [];
        return myList
          .filter(Boolean)
          .map((task) =>
            normalizeTaskRecord(
              {
                ...task,
                hierarchyLevel: task.hierarchyLevel ?? 0,
              },
              { isSelfTask: true }
            )
          )
          .filter(Boolean);
      } catch (selfErr) {
        console.error("Error fetching own tasks:", selfErr);
        return [];
      }
    };

    const loadTasks = async () => {
      try {
        const res = await fetch(getApiUrl("api/task/subordinates"), {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to load subordinate tasks (${res.status})`);
        }
        const payload = await res.json();
        if (cancelled) return;

        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.tasks)
          ? payload.tasks
          : [];

        const subordinateTasks = list
          .filter((task) => task && (task.hierarchyLevel === 1 || task.hierarchyLevel === 2))
          .map((task) => normalizeTaskRecord(task))
          .filter(Boolean);

        const selfTasks = await fetchOwnTasks();

        const merged = [...subordinateTasks, ...selfTasks];
        const dedup = new Map();
        merged.forEach((task) => {
          const key = task?.id
            ? String(task.id)
            : `${task?.normalizedAssigneeId || ""}-${task?.title || ""}-${task?.created_at || Math.random()}`;
          if (!dedup.has(key)) {
            dedup.set(key, task);
          }
        });

        if (!cancelled) {
          setTasks(Array.from(dedup.values()));
        }
      } catch (err) {
        console.error("Error fetching subordinate tasks:", err);
        if (!cancelled) {
          const fallbackSelfTasks = await fetchOwnTasks();
          setTasks(fallbackSelfTasks);
        }
      }
    };

    loadTasks();
    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  const filteredTasks = useMemo(
    () => filterTasks(tasks, filters),
    [tasks, filters]
  );

  // Calculate summary data
  const statusSummary = useMemo(() => {
    const statusCounts = {
      Pending: 0,
      Planned: 0,
      Delegated: 0,
      WIP: 0,
      Completed: 0,
      Revalidate: 0,
    };

    filteredTasks.forEach((task) => {
      const status = task.status || "Pending";
      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      }
    });

    return [
      { label: "Pending", value: statusCounts["Pending"], color: "#a285e4" },
      { label: "Planned", value: statusCounts["Planned"], color: "#3b8beb" },
      {
        label: "Delegated",
        value: statusCounts["Delegated"],
        color: "#ffb325",
      },
      { label: "WIP", value: statusCounts["WIP"], color: "#ff9999" },
      {
        label: "Completed",
        value: statusCounts["Completed"],
        color: "#4ad24a",
      },
      {
        label: "Revalidate",
        value: statusCounts["Revalidate"],
        color: "#f09be0",
      },
    ];
  }, [filteredTasks]);

  // Calculate department-wise data
  const deptData = useMemo(() => {
    const deptPending = [];
    const deptWIP = [];
    const deptCompleted = [];

    // Group tasks by department
    const deptGroups = {};
    filteredTasks.forEach((task) => {
      const deptId = task.normalizedDepartmentId
        ? String(task.normalizedDepartmentId)
        : "__UNASSIGNED__";
      const deptName = task.normalizedDepartmentId
        ? departments.find((d) => String(d.id) === deptId)?.name ||
          "Unknown Department"
        : "No Department";

      if (!deptGroups[deptId]) {
        deptGroups[deptId] = {
          name: deptName,
          pending: 0,
          wip: 0,
          completed: 0,
        };
      }

      const status = task.status || "Pending";
      if (status === "Pending") deptGroups[deptId].pending++;
      else if (status === "WIP") deptGroups[deptId].wip++;
      else if (status === "Completed") deptGroups[deptId].completed++;
    });

    // Convert to chart format
    Object.values(deptGroups).forEach((dept) => {
      if (dept.pending > 0) {
        deptPending.push({ dept: dept.name, value: dept.pending });
      }
      if (dept.wip > 0) {
        deptWIP.push({ dept: dept.name, value: dept.wip });
      }
      if (dept.completed > 0) {
        deptCompleted.push({ dept: dept.name, value: dept.completed });
      }
    });

    return { deptPending, deptWIP, deptCompleted };
  }, [filteredTasks, departments]);

  // Calculate monthly trend data
  const monthlyData = useMemo(() => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const currentYear = new Date().getFullYear();

    const monthlyStats = months.map((month, index) => {
      const monthTasks = filteredTasks.filter((task) => {
        const taskDate = new Date(task.created_at || task.due_date);
        return (
          taskDate.getMonth() === index &&
          taskDate.getFullYear() === currentYear
        );
      });

      const statusCounts = {
        Pending: 0,
        Planned: 0,
        Delegated: 0,
        WIP: 0,
        Completed: 0,
        Revalidate: 0,
      };

      monthTasks.forEach((task) => {
        const status = task.status || "Pending";
        if (statusCounts.hasOwnProperty(status)) {
          statusCounts[status]++;
        }
      });

      return {
        month: `${month}-${currentYear.toString().slice(-2)}`,
        ...statusCounts,
      };
    });

    return monthlyStats;
  }, [filteredTasks]);

  // Filter tasks based on current filters
  function filterTasks(tasks, filters) {
    return tasks.filter((task) => {
      // Date filter
      if (filters.fromDate) {
        const dateSource =
          task.start_date || task.created_at || task.due_date || null;
        const taskDate = dateSource ? new Date(dateSource) : null;
        const fromDate = new Date(filters.fromDate);
        if (taskDate && !Number.isNaN(taskDate.getTime()) && taskDate < fromDate)
          return false;
      }

      if (filters.toDate) {
        const dateSource =
          task.start_date || task.created_at || task.due_date || null;
        const taskDate = dateSource ? new Date(dateSource) : null;
        const toDate = new Date(filters.toDate);
        if (taskDate && !Number.isNaN(taskDate.getTime()) && taskDate > toDate)
          return false;
      }

      // Department filter
      if (
        filters.department &&
        task.normalizedDepartmentId !== filters.department
      ) {
        return false;
      }

      // Employee filter
      if (
        filters.employee &&
        task.normalizedAssigneeId !== filters.employee
      ) {
        return false;
      }

      return true;
    });
  }

  // Filter change handler
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Search handler
  const handleSearch = () => {
    // Filters are applied automatically through useMemo
  };

  // Reset handler
  const handleReset = () => {
    setFilters((prev) => ({
      ...prev,
      fromDate: "",
      toDate: "",
      department: "",
      employee: "",
    }));
  };

  // Loader effect
  useEffect(() => {
    if (!showLoader) {
      const timer = setTimeout(() => setShowCharts(true), 150);
      return () => clearTimeout(timer);
    } else {
      setShowCharts(false);
    }
  }, [showLoader]);

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <DashboardLayout>
      <div className="dashboard-container">
        {showLoader && (
          <div className="v-loader-overlay">
            <svg
              className="v-loader-svg v-loader-animate"
              width="120"
              height="120"
              viewBox="0 0 120 120"
            >
              <defs>
                <linearGradient
                  id="v-gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#e30613" stopOpacity="1" />
                  <stop offset="50%" stopColor="#ff4757" stopOpacity="1" />
                  <stop offset="100%" stopColor="#e30613" stopOpacity="1" />
                </linearGradient>
                <filter
                  id="v-glow"
                  x="-30%"
                  y="-30%"
                  width="160%"
                  height="160%"
                >
                  <feDropShadow
                    dx="0"
                    dy="0"
                    stdDeviation="4"
                    floodColor="#e30613"
                    floodOpacity="0.3"
                  />
                  <feDropShadow
                    dx="0"
                    dy="0"
                    stdDeviation="8"
                    floodColor="#e30613"
                    floodOpacity="0.15"
                  />
                </filter>
              </defs>
              <g className="v-loader-glow">
                <path
                  className="v-loader-path"
                  d="M 35 40 L 60 80 L 85 40"
                  fill="none"
                  strokeWidth="12"
                  strokeLinecap="round"
                  filter="url(#v-glow)"
                />
              </g>
              <circle
                cx="60"
                cy="60"
                r="45"
                fill="none"
                stroke="rgba(227, 6, 19, 0.1)"
                strokeWidth="2"
                strokeDasharray="5,5"
                opacity="0.6"
              />
            </svg>
          </div>
        )}

        {/* Filter Bar */}
        {!showLoader && (
          <FilterBar
            filters={filters}
            departments={departments}
            employees={managedEmployees}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onReset={handleReset}
          />
        )}

        {/* Status Summary Cards */}
        {!showLoader && <StatusSummaryCards summary={statusSummary} />}

        {/* Main Task Status Chart */}
        {!showLoader && <h3>Task Status Trend</h3>}
        {showCharts && (
          <div
            style={{ transition: "opacity 0.3s", opacity: showCharts ? 1 : 0 }}
          >
            <MainTaskStatusChart data={monthlyData} />
          </div>
        )}

        {/* Department Wise Charts */}
        {showCharts && (
          <DeptChartContainer title="Department Wise Pending Tasks">
            <DeptBarChart data={deptData.deptPending} color="#a285e4" />
          </DeptChartContainer>
        )}

        {showCharts && (
          <DeptChartContainer title="Department Wise WIP Tasks">
            <DeptBarChart data={deptData.deptWIP} color="#ff9933" />
          </DeptChartContainer>
        )}

        {showCharts && (
          <DeptChartContainer title="Department Wise Completed Tasks">
            <DeptBarChart data={deptData.deptCompleted} color="#4ad24a" />
          </DeptChartContainer>
        )}
      </div>
    </DashboardLayout>
  );
};

export default React.memo(Dashboard);
